import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, describeError } from "../lib/api";
import { authClient } from "../lib/auth";
import { rememberSignedIn, safeNext } from "../lib/returnTo";
import { SignInButtons } from "../components/SignIn";
import { Wordmark } from "../components/Wordmark";
import { CodeBoxes } from "../components/CodeBoxes";

/*
 * /signin: one page for new and returning people. Prebuilt to plain HTML like
 * the home page (prerender.tsx), so nothing here uses the router. `next` is
 * read when a button is pressed and only ever a path on this site (safeNext);
 * first sign-ins pass through /welcome before reaching it (useWelcomeGate).
 *
 * Below the providers, a code by email (PRD "Email addresses and email sign-in"): the
 * address, then six boxes. It shows unless the server says it can't send email, so the
 * prebuilt page and the first render agree.
 */
const nextFromUrl = () => safeNext(new URLSearchParams(window.location.search).get("next"));
const RESEND_AFTER = 30;

export function SignInPage() {
  // Already signed in: on to where you were heading.
  useEffect(() => {
    void authClient.getSession().then(({ data }) => {
      rememberSignedIn(Boolean(data));
      if (data) window.location.replace(nextFromUrl());
    }).catch(() => { /* offline: the buttons still show */ });
  }, []);
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, staleTime: 5 * 60_000 });
  const emailSignIn = health.data?.emailSignIn ?? true;

  return (
    <main className="page landing signin-page">
      <a href="/" className="signin-wm" aria-label="Pagemates home"><Wordmark size="lg" /></a>
      <EmailOrProviders emailSignIn={emailSignIn} />
      <p className="muted small center"><a href="/policy.html">Policy and contact</a></p>
    </main>
  );
}

function EmailOrProviders({ emailSignIn }: { emailSignIn: boolean }) {
  const [step, setStep] = useState<"start" | "code">("start");
  const [email, setEmail] = useState("");
  // "Send a new code" from an expired link comes back with the address filled in (after hydration, so the prebuilt page matches).
  useEffect(() => { const e = new URLSearchParams(window.location.search).get("email"); if (e) setEmail(e); }, []);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async () => {
    setBusy(true); setErr(null);
    try { await api.emailCodeSend(email.trim(), nextFromUrl()); setStep("code"); setCode(""); setWait(RESEND_AFTER); }
    catch (e) { setErr(describeError(e)); }
    finally { setBusy(false); }
  };
  const verify = async (c: string) => {
    setBusy(true); setErr(null);
    try { await api.emailCodeVerify(email.trim(), c); window.location.replace(nextFromUrl()); }
    catch (e) { setErr(describeError(e)); setCode(""); setBusy(false); }
  };

  if (step === "code") return (
    <div className="stack code-step">
      <div className="stack-sm">
        <h1>Check your email</h1>
        <p style={{ margin: 0 }}>We sent a code to <b>{email.trim()}</b>.</p>
      </div>
      <CodeBoxes value={code} onChange={setCode} onComplete={(c) => void verify(c)} disabled={busy} autoFocus />
      <p className="muted small" style={{ margin: 0 }}>It signs you in as soon as the sixth digit is in. It works once, for 10 minutes.</p>
      {err && <p className="error small" role="alert">{err}</p>}
      <div className="stack-sm" style={{ alignItems: "center" }}>
        <button type="button" className="btn" disabled={wait > 0 || busy} onClick={() => void send()}>{wait > 0 ? `Send a new code in 0:${String(wait).padStart(2, "0")}` : "Send a new code"}</button>
        <button type="button" className="linkish" onClick={() => { setStep("start"); setErr(null); }}>Use a different email</button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>The email's "Sign in" button works too, in whichever browser it opens. Nothing there? Check spam, or the address above.</p>
    </div>
  );

  return (
    <>
      <h1>Sign in to Pagemates</h1>
      <SignInButtons next={nextFromUrl} assume={["discord", "google", "apple"]} />
      {emailSignIn && <>
        <div className="or" aria-hidden="true"><span />or<span /></div>
        <form className="stack-sm email-signin" noValidate onSubmit={(e) => { e.preventDefault(); if (email.trim()) void send(); else setErr("Enter your email."); }}>
          <label htmlFor="signin-email" className="small" style={{ fontWeight: 600, textAlign: "left" }}>Your email</label>
          <input id="signin-email" className="input" type="email" autoComplete="email" inputMode="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setErr(null); }} />
          <button type="submit" className="btn primary block" disabled={busy}>{busy ? "Sending…" : "Email me a code"}</button>
          {err && <p className="error small" role="alert">{err}</p>}
        </form>
      </>}
      <p className="muted small center">New here? Any of these makes your account. <a href="/">What is Pagemates?</a></p>
    </>
  );
}
